// Drop-in replacement for a text input that accepts both keyboard-wedge scanner
// input (typed naturally into the focused field, terminated by Enter) and
// camera scans (small camera-icon button on the right opens BarcodeScannerModal).
//
// The camera modal is lazy-loaded so the ZXing bundle (~150KB) only ships when
// a user actually requests a camera scan.
//
// onSubmit receives the *current* value as a parameter so callers don't have
// to wait for parent state to flush after a scan.

import React, { Suspense, lazy, useCallback, useState } from 'react';
import { IconButton } from '../ui/Button';
import Field from '../ui/Field';
import Tooltip from '../ui/Tooltip';
import type { BarcodeFormat, ScanResult } from './types';

const BarcodeScannerModal = lazy(() => import('./BarcodeScannerModal'));

interface Props {
  value: string;
  onChange: (value: string) => void;
  onScan?: (result: ScanResult) => void;
  onSubmit?: (value: string) => void;
  /** When true, a successful camera scan auto-fires onSubmit with the scanned value. */
  autoSubmitOnScan?: boolean;
  formats?: BarcodeFormat[];
  scanTitle?: string;
  scanHint?: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  /** Extra classes for the field itself. */
  inputClassName?: string;
  id?: string;
  /** Hides the camera button (for cases where only wedge/typed input is wanted). */
  cameraDisabled?: boolean;
}

const CameraIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const ScanInput: React.FC<Props> = ({
  value,
  onChange,
  onScan,
  onSubmit,
  autoSubmitOnScan = false,
  formats,
  scanTitle,
  scanHint,
  placeholder,
  autoFocus,
  disabled,
  className = '',
  inputClassName = '',
  id,
  cameraDisabled,
}) => {
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScan = useCallback(
    (result: ScanResult) => {
      onChange(result.text);
      onScan?.(result);
      if (autoSubmitOnScan) {
        // Pass the scanned value directly so callers don't read stale parent state.
        onSubmit?.(result.text);
      }
    },
    [onChange, onScan, autoSubmitOnScan, onSubmit],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.(value);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Field
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        className={`pr-10 font-mono ${inputClassName}`}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />
      {!cameraDisabled && (
        <Tooltip label="Scan with camera">
          <IconButton
            label="Scan with camera"
            size="sm"
            onClick={() => setScannerOpen(true)}
            disabled={disabled}
            className="absolute top-1/2 right-2 -translate-y-1/2"
          >
            <CameraIcon />
          </IconButton>
        </Tooltip>
      )}
      {scannerOpen && (
        <Suspense fallback={null}>
          <BarcodeScannerModal
            open={scannerOpen}
            onClose={() => setScannerOpen(false)}
            onScan={handleScan}
            formats={formats}
            title={scanTitle}
            hint={scanHint}
          />
        </Suspense>
      )}
    </div>
  );
};

export default ScanInput;
